import { useState } from "react";
import { Box, Check, Clock, AlertCircle, Loader2, Trash2 } from "lucide-react";
import { getBackendUrl } from "@/lib/backend";

const BACKEND = getBackendUrl();

const RUNNING_STATES = new Set(["running", "queued"]);
const FINISHED_STATES = new Set(["success", "failed", "cancelled", "expired", "banned"]);

/**
 * Thumbnail tile inside the CAD dialog's "Previous 3D meshes" grid.
 *
 * Behaviour
 * ---------
 * - Click → load the active job (disabled if mesh not ready).
 * - Hover → reveals the small trash button (top-right). Deleting is
 *   blocked while the job is still `running`/`queued` to avoid wasting
 *   credits on a Tripo task that's about to succeed.
 * - Running jobs show a live progress badge (poll-driven by parent).
 *
 * URL handling
 * ------------
 * `job.rendered_image_url` can be:
 *  1. Relative `/api/user-images/<uid>/cad_<job>_preview.png` (backend
 *     stored copy, never expires) → prepend BACKEND.
 *  2. Legacy absolute Tripo CDN URL → may have expired; `onError`
 *     falls back to the placeholder.
 *  3. null/undefined → render the Box placeholder.
 */
export default function HistoryTile({ job, isActive, onClick, onDelete }) {
  const [imgError, setImgError] = useState(false);
  const status = (job.status || "").toLowerCase();
  const running = RUNNING_STATES.has(status);
  const failed = ["failed", "cancelled", "expired", "banned"].includes(status);
  const canDelete = FINISHED_STATES.has(status);
  const rawUrl = job.rendered_image_url;
  const resolvedUrl = rawUrl
    ? (rawUrl.startsWith("http") ? rawUrl : `${BACKEND}${rawUrl}`)
    : null;

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    if (!canDelete) return;
    if (onDelete) onDelete();
  };

  return (
    <div
      data-testid={`cad-history-${job.job_id}`}
      className={`group relative aspect-square rounded-lg overflow-hidden border transition ${
        isActive ? "border-[#D4AF37]/70 ring-1 ring-[#D4AF37]/40"
                 : "border-white/10 hover:border-white/30"
      }`}
      title={`${job.status} · ${new Date(job.created_at).toLocaleString()}`}
    >
      <button
        type="button"
        onClick={onClick}
        disabled={!job.mesh_ready}
        className="absolute inset-0 w-full h-full disabled:cursor-not-allowed"
      >
        {resolvedUrl && !imgError ? (
          <img
            src={resolvedUrl}
            alt="3D mesh"
            onError={() => setImgError(true)}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 bg-[#0A0A0A] flex items-center justify-center">
            {running
              ? <Loader2 className="w-5 h-5 text-[#D4AF37] animate-spin" />
              : <Box className="w-5 h-5 text-white/25" />}
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none" />
        <span className="absolute bottom-1 left-1.5 text-[8.5px] uppercase tracking-[0.18em] text-[#D4AF37]/85">
          {new Date(job.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}
        </span>
      </button>

      {running && (
        <span
          className="absolute top-1 left-1 inline-flex items-center gap-1 text-[8.5px] uppercase tracking-[0.18em] text-amber-200 bg-black/70 px-1.5 py-0.5 rounded-full pointer-events-none"
          data-testid={`cad-history-${job.job_id}-progress`}
        >
          <Loader2 className="w-2 h-2 animate-spin" />
          {Number.isFinite(job.progress) && job.progress > 0
            ? `${Math.round(job.progress)}%`
            : status}
        </span>
      )}
      {failed && (
        <span className="absolute top-1 left-1 inline-flex items-center gap-1 text-[8.5px] uppercase tracking-[0.18em] text-red-300 bg-black/70 px-1.5 py-0.5 rounded-full pointer-events-none">
          <AlertCircle className="w-2 h-2" /> Failed
        </span>
      )}
      {isActive && (
        <span className="absolute bottom-1 right-1 inline-flex items-center gap-1 text-[8.5px] uppercase tracking-[0.18em] text-emerald-300 bg-black/70 px-1.5 py-0.5 rounded-full pointer-events-none">
          <Check className="w-2 h-2" /> Viewing
        </span>
      )}

      {/* Delete (×) — only visible on hover, blocked while running so
          users don't accidentally trash a job that's about to succeed. */}
      {onDelete && (
        <button
          type="button"
          onClick={handleDeleteClick}
          disabled={!canDelete}
          data-testid={`cad-history-${job.job_id}-delete`}
          title={canDelete
            ? "Delete this mesh"
            : "Wait for the job to finish before deleting"}
          className={`absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center transition opacity-0 group-hover:opacity-100 ${
            canDelete
              ? "bg-black/70 text-rose-300 hover:bg-rose-500/30 hover:text-white border border-rose-400/40 cursor-pointer"
              : "bg-black/55 text-white/35 border border-white/15 cursor-not-allowed"
          }`}
        >
          <Trash2 className="w-2.5 h-2.5" />
        </button>
      )}

      {running && !job.rendered_image_url && (
        <Clock className="absolute bottom-1 right-1 w-2.5 h-2.5 text-white/45 pointer-events-none" />
      )}
    </div>
  );
}
